/* eslint-disable */
import axios from "axios";
import { AUTH_CONSTANTS } from "./auth";

const instance = axios.create({
	baseURL: `${process.env.REACT_APP_BACKEND_URL}/accounts/${process.env.REACT_APP_ACCOUNT_ID}`,
	headers: {
		"Content-Type": "application/json",
	},
});

const instanceBo = axios.create({
	baseURL: `${process.env.REACT_APP_BACKEND_URL_BO}/accounts/${process.env.REACT_APP_ACCOUNT_ID}`,
	headers: {
		"Content-Type": "application/json",
	},
});

const setBaseURL = (url: string): void => {
	instance.defaults.baseURL = url;
};

const setToken = (token: string): void => {
	instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

const setBaseURLBo = (url: string): void => {
	instanceBo.defaults.baseURL = url;
};

const setTokenBo = (token: string): void => {
	instanceBo.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

type TokenRefresher = () => Promise<string>;
type SessionExpiredHandler = () => void;

let _refreshToken: TokenRefresher | null = null;
let _onSessionExpired: SessionExpiredHandler | null = null;
let _isRefreshing = false;
let _isInitialAuth = false;
let _refreshSubscribers: Array<(token: string) => void> = [];

const onTokenRefreshed = (token: string) => {
	_refreshSubscribers.forEach((cb) => cb(token));
	_refreshSubscribers = [];
};

const addRefreshSubscriber = (cb: (token: string) => void) => {
	if (_refreshSubscribers.length < AUTH_CONSTANTS.MAX_REFRESH_SUBSCRIBERS) {
		_refreshSubscribers.push(cb);
	} else {
		console.warn(
			"[Auth] Too many concurrent refresh requests, rejecting subscriber",
		);
		// Reject the promise for this subscriber
		cb("");
	}
};

const setInitialAuthFlag = (isInitialAuth: boolean) => {
	_isInitialAuth = isInitialAuth;
};

const setup401Interceptor = (
	refreshToken: TokenRefresher,
	onSessionExpired: SessionExpiredHandler,
) => {
	_refreshToken = refreshToken;
	_onSessionExpired = onSessionExpired;
};

const create401Interceptor = (axiosInstance: typeof instance) => {
	axiosInstance.interceptors.response.use(
		(response) => response,
		async (error) => {
			const originalRequest = error.config;

			// Skip automatic token refresh during initial authentication
			if (_isInitialAuth && error.response?.status === 401) {
				console.log("[Auth] 401 during initial auth - rejecting without retry");
				return Promise.reject(error);
			}

			if (
				error.response?.status === 401 &&
				!originalRequest._retry &&
				_refreshToken
			) {
				if (_isRefreshing) {
					return new Promise((resolve) => {
						addRefreshSubscriber((token: string) => {
							originalRequest.headers["Authorization"] = `Bearer ${token}`;
							resolve(axiosInstance(originalRequest));
						});
					});
				}

				originalRequest._retry = true;
				_isRefreshing = true;

				try {
					const newToken = await _refreshToken();
					setToken(newToken);
					setTokenBo(newToken);
					onTokenRefreshed(newToken);
					originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
					return axiosInstance(originalRequest);
				} catch {
					_onSessionExpired?.();
					return Promise.reject(error);
				} finally {
					_isRefreshing = false;
				}
			}
			return Promise.reject(error);
		},
	);
};

create401Interceptor(instance);
create401Interceptor(instanceBo);

const transformAxiosError = (error: Error) => {
	if (axios.isAxiosError(error)) {
		// Handle CORS errors and network errors where response is undefined
		if (!error.response) {
			return {
				message: error.message || "Network error occurred",
				error: "NetworkError",
				code: error.code,
			};
		}

		return {
			message:
				error.response?.data?.error?.errorMessage ||
				error.response?.data?.message ||
				error.message,
			error: error.response?.data?.error || "Unknown error",
		};
	}
	return error;
};

export {
	instance as axios,
	instanceBo,
	setBaseURL,
	setToken,
	setBaseURLBo,
	setTokenBo,
	transformAxiosError,
	setup401Interceptor,
	setInitialAuthFlag,
};
